import { useTranslation } from 'react-i18next'
import { withQuery } from 'ufo'
import { normalizeLocale } from 'src/i18n/utils'
import { getBaseLanguageCode } from 'src/utils/tack'

const AGENT_MIO_BASE_URL = 'https://agent.pixai.art'

// Maps pixai.art base locale codes to the locale path segment used by
// agent.pixai.art. Locales not listed here fall through to the default
// (no prefix).
const AGENT_MIO_LOCALE_PATHS: Record<string, string> = {
  ja: 'ja',
  ko: 'ko',
  zh: 'zh-TW',
  es: 'es',
}

export interface MioDeepLinkParams {
  prompt?: string
  autoSubmit?: boolean
  mediaId?: string
  /** Placement label for analytics (`utm_content`), e.g. `menu_bar`. */
  utmContent?: string
  /** Override `utm_source`. Defaults to `pixai`. */
  utmSource?: string
}

const DEFAULT_UTM_SOURCE = 'pixai'

/**
 * Build the agent.pixai.art URL for a given pixai.art locale.
 *
 * Normalizes region-tagged codes (e.g. `zh-TW`, `es-MX`) and locale aliases
 * (e.g. `kr` → `ko`) before looking up the agent path prefix. Locales not in
 * `AGENT_MIO_LOCALE_PATHS` fall back to the unprefixed base URL.
 *
 * Always appends `utm_source=pixai` (overridable via `utmSource`) so analytics
 * on agent.pixai.art can attribute traffic. Pass `utmContent` to tag the
 * specific placement (menu_bar, gen_page_tab, etc).
 */
export function getAgentMioUrl(
  locale: string | undefined,
  params?: MioDeepLinkParams,
): string {
  const baseLang = locale ? getBaseLanguageCode(locale) : ''
  // Resolve aliases (e.g. `kr` → `ko`) so they pick up the mapped prefix.
  const resolved = normalizeLocale(baseLang) ?? baseLang
  const localePath = AGENT_MIO_LOCALE_PATHS[resolved]
  const url = localePath
    ? `${AGENT_MIO_BASE_URL}/${localePath}`
    : AGENT_MIO_BASE_URL
  const { utmSource = DEFAULT_UTM_SOURCE, utmContent, ...rest } = params ?? {}
  return withQuery(url, {
    utm_source: utmSource,
    ...(utmContent ? { utm_content: utmContent } : {}),
    ...rest,
  })
}

// agent.pixai.art currently ignores deep-link params (prompt, mediaId). The
// `params` arg is kept so call sites can start passing them once supported.
export function useAgentMioUrl(params?: MioDeepLinkParams): string {
  const { i18n } = useTranslation()
  return getAgentMioUrl(i18n.language, params)
}

/**
 * Append utm params to an existing Mio Agent URL (e.g. one resolved from an
 * i18n string). Use when the URL itself is owned by a translation/config
 * source rather than constructed via {@link getAgentMioUrl}.
 */
export function withMioUtm(
  url: string,
  params?: Pick<MioDeepLinkParams, 'utmContent' | 'utmSource'>,
): string {
  const { utmSource = DEFAULT_UTM_SOURCE, utmContent } = params ?? {}
  return withQuery(url, {
    utm_source: utmSource,
    ...(utmContent ? { utm_content: utmContent } : {}),
  })
}

const AGENT_MIO_HOST_RE = /^https?:\/\/agent\.pixai\.art(?:[/?#]|$)/i

/** Whether the URL points to the Mio Agent web app. */
export function isAgentMioUrl(url: string | null | undefined): boolean {
  return !!url && AGENT_MIO_HOST_RE.test(url)
}
